/**
 * Community listings API (Camp Essentials > Gear > Listings).
 */
import api from '../../../utils/api';
import { buildMainImage, buildThumbnail } from '../utils/listingPhotoUtils';

// Django and DRF expect trailing slashes (APPEND_SLASH); POST without slash loses body on redirect.
const BASE = '/community-listings/';

export const communityListingsService = {
  list(params = {}) {
    return api.get(BASE, { params }).then((res) => res.data);
  },

  get(id) {
    return api.get(`${BASE}${id}/`).then((res) => res.data);
  },

  create(payload) {
    return api.post(BASE, payload).then((res) => res.data);
  },

  update(id, payload) {
    return api.patch(`${BASE}${id}/`, payload).then((res) => res.data);
  },

  delete(id) {
    return api.delete(`${BASE}${id}/`);
  },

  addPhoto(listingId, payload) {
    return api.post(`${BASE}${listingId}/add-photo/`, payload).then((res) => res.data);
  },

  /**
   * Get presigned URLs for listing photo upload.
   * Body: scope: 'listing-photo', listing_id, filename, content_type.
   */
  initListingPhotoUpload(listingId, filename, contentType = 'image/jpeg') {
    return api
      .post('/assets/init/', {
        scope: 'listing-photo',
        listing_id: listingId,
        filename: filename || 'photo.jpg',
        content_type: contentType,
      })
      .then((res) => res.data);
  },

  /**
   * Upload one image: resize, PUT to Spaces, then add-photo.
   * Returns the added photo payload or throws.
   */
  async uploadListingPhoto(listingId, file, position = 1) {
    const contentType = file.type || 'image/jpeg';
    const init = await this.initListingPhotoUpload(listingId, file.name, contentType);
    if (!init || !init.upload_url || !init.object_key) {
      throw new Error('Failed to get upload URL');
    }
    const [main, thumb] = await Promise.all([
      buildMainImage(file),
      buildThumbnail(file),
    ]);
    if (main.blob.size > 10 * 1024 * 1024) {
      throw new Error('Image is too large (max 10 MB after resize)');
    }
    await fetch(init.upload_url, {
      method: 'PUT',
      body: main.blob,
      headers: { 'Content-Type': contentType },
    });
    if (init.thumb_upload_url && init.thumb_object_key) {
      await fetch(init.thumb_upload_url, {
        method: 'PUT',
        body: thumb.blob,
        headers: { 'Content-Type': contentType },
      });
    }
    const added = await this.addPhoto(listingId, {
      object_key: init.object_key,
      thumb_object_key: init.thumb_object_key || undefined,
      position,
      width: main.width,
      height: main.height,
      size_bytes: main.blob.size,
    });
    return added;
  },
};
